import { z } from 'zod';
import { openWorldCommandIdSchema } from './crossing';
import {
  OPEN_WORLD_DEATH_BOX_VERSION,
  OPEN_WORLD_MAX_CARGO_SLOTS,
  openWorldFieldItemSchema,
} from './field-cargo';

/**
 * **Uma caixa de morte, e o que se diz sobre ela.**
 *
 * ## O que uma caixa é
 *
 * Tudo o que um piloto trazia, no sítio exato onde ele morreu, à vista de toda a
 * gente. Não tem dono, não tem prioridade e não tem fila: o assassino, um
 * terceiro que passava, um companheiro de PT e a própria vítima que voltasse
 * chegam à caixa com exatamente os mesmos direitos, que são nenhuns e todos.
 *
 * ## Quatro coisas que ela deliberadamente não faz
 *
 * - **não se move.** Fica onde a morte aconteceu, e é isso que a torna um sítio
 *   e não um objeto;
 * - **não pode ser destruída.** Ninguém a pode negar aos outros;
 * - **não bloqueia passagem.** Não é um obstáculo, não empurra ninguém e não
 *   aparece na lista de colisões — uma caixa que travasse cascos seria uma
 *   parede que a morte de alguém deixou no meio do caminho;
 * - **não protege quem a saqueia.** Um piloto a saquear continua a voar, a
 *   levar dano e a poder morrer, e é por isso que saquear é uma decisão.
 *
 * ## Quinze minutos, e depois desaparece
 *
 * O que sobra ao fim dos quinze minutos **perde-se**. Não volta para ninguém,
 * não vai para lado nenhum, e é isso que faz uma caixa ser uma oportunidade e
 * não um armazém. Uma caixa vazia desaparece imediatamente, porque o que ela era
 * já não está lá.
 */

/* -------------------------------------------------------------------------- */
/*  As constantes                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Quanto tempo uma caixa dura: **quinze minutos**, em tempo de relógio.
 *
 * Deliberadamente **não** no relógio observado da sala, e a diferença importa.
 * A janela de reconexão conta tempo observado porque uma falha do runtime não
 * pode gastar os três minutos de alguém — o jogador não estava lá para os usar.
 * Uma caixa é o contrário: ela é pública, e o que a torna uma oportunidade é
 * que ela acaba. Uma caixa cujo prazo parasse enquanto o quadrante estava vazio
 * seria uma caixa que espera pelo saqueador, e uma paragem de runtime passaria a
 * estender espólio.
 *
 * Por isso o prazo é um instante absoluto, escrito na linha, e sobrevive a um
 * quadrante vazio e a um reinício exatamente como está.
 */
export const OPEN_WORLD_DEATH_BOX_TTL_MS = 15 * 60_000;

/**
 * A distância a que uma caixa pode ser aberta: **um segundo de voo**.
 *
 * Na mesma unidade e no mesmo idioma que a banda de fronteira e a folga de
 * chegada de um grupo — segundos de voo à velocidade-base — porque é assim que
 * uma distância deste mundo se lê. **Provisório**, como as outras duas.
 *
 * O número em si vive em `game-domain`, ao lado das outras distâncias do
 * quadrante; aqui fica só o que ele significa.
 */
export const OPEN_WORLD_LOOT_RADIUS_SECONDS = 1;

/* -------------------------------------------------------------------------- */
/*  O que o cliente pode dizer                                                */
/* -------------------------------------------------------------------------- */

/** O id de uma caixa. Um uuid, opaco, e nunca uma posição na lista de nada. */
export const openWorldBoxIdSchema = z.uuid();

/** O id de um item de campo, como ele viaja. */
export const openWorldFieldItemIdSchema = z.uuid();

/**
 * **Abrir uma caixa.**
 *
 * O cliente diz qual, e mais nada. Quem ele é e onde está vêm do socket: uma
 * intenção que carregasse a posição do jogador seria uma intenção em que o
 * jogador escolhe a distância a que consegue saquear.
 */
export const openWorldLootOpenSchema = z
  .object({
    t: z.literal('lo'),
    seq: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    id: openWorldCommandIdSchema,
    b: openWorldBoxIdSchema,
  })
  .strict();

/**
 * **Levar um item, aquele item.**
 *
 * A ordem carrega a sua própria identidade, e é ela que faz um reenvio ser lido
 * em vez de obedecido duas vezes. Sem isso, um cliente honesto que não viu a
 * resposta e voltou a pedir levaria dois itens — ou, pior, o segundo pedido
 * falharia por falta de espaço e o jogador julgaria que perdeu o primeiro.
 */
export const openWorldLootTakeSchema = z
  .object({
    t: z.literal('lt'),
    seq: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    id: openWorldCommandIdSchema,
    b: openWorldBoxIdSchema,
    /** Qual item. O id de campo, que é o mesmo desde que o item foi concedido. */
    f: openWorldFieldItemIdSchema,
  })
  .strict();

/**
 * **Levar tudo o que couber.**
 *
 * Não é "levar tudo": é levar o que cabe, pela ordem canónica, sem substituir
 * nada do que já lá está. O que ficou é dito de volta com clareza, porque um
 * jogador que carrega neste botão e vê a caixa continuar cheia tem de saber
 * porquê.
 *
 * O ouro entra sempre por inteiro, porque o ouro não ocupa espaço.
 */
export const openWorldLootTakeAllSchema = z
  .object({
    t: z.literal('la'),
    seq: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    id: openWorldCommandIdSchema,
    b: openWorldBoxIdSchema,
  })
  .strict();

/** **Levar o ouro.** Todo, porque não há meio-ouro e porque não ocupa espaço. */
export const openWorldLootGoldSchema = z
  .object({
    t: z.literal('lg'),
    seq: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    id: openWorldCommandIdSchema,
    b: openWorldBoxIdSchema,
  })
  .strict();

/** **Fechar.** Sem id de ordem: fechar não move nada, e repetir é o mesmo que fechar. */
export const openWorldLootCloseSchema = z
  .object({
    t: z.literal('lc'),
    seq: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
  })
  .strict();

/* -------------------------------------------------------------------------- */
/*  O que o servidor responde                                                 */
/* -------------------------------------------------------------------------- */

/**
 * **Porque não.** Uma lista fechada, e nenhuma delas é uma exceção.
 *
 * Cada uma é uma coisa que o servidor sabe e o cliente não tinha como saber no
 * instante em que perguntou — que outro jogador chegou primeiro, que a caixa
 * expirou entre a trama e o pedido, que o casco saiu do raio enquanto o pedido
 * viajava.
 */
export const OPEN_WORLD_LOOT_REFUSALS = [
  /** A caixa não existe, já esvaziou ou já expirou. */
  'no_box',
  /** Está noutro quadrante. Uma caixa é um sítio, e o sítio é aquele. */
  'wrong_quadrant',
  /** Demasiado longe. O raio é do servidor, e o cliente não o propõe. */
  'out_of_range',
  /** O casco está destruído. Um destroço não saqueia. */
  'destroyed',
  /** Este item já não está na caixa: alguém chegou primeiro. */
  'already_taken',
  /** Não há espaço na carga. A resposta diz quantos espaços faltam. */
  'no_space',
  /** Não há ouro nenhum nesta caixa. */
  'no_gold',
  /** Este socket não está a saquear esta caixa. Abra-a primeiro. */
  'not_looting',
  /** A autoridade que move propriedade não respondeu. Nada mudou. */
  'unavailable',
] as const;
export type OpenWorldLootRefusal = (typeof OPEN_WORLD_LOOT_REFUSALS)[number];

/**
 * Porque uma interação de saque terminou.
 *
 * **Quase tudo termina uma interação**, e é deliberado: saquear é uma coisa que
 * se faz parado e a olhar para uma caixa, e qualquer outra coisa que o jogador
 * faça — ou que lhe façam — é ele a decidir que já não está a fazer isto.
 */
export const OPEN_WORLD_LOOT_END_REASONS = [
  /** O jogador fechou. */
  'closed',
  /** Mexeu-se. */
  'moved',
  /** Disparou. */
  'fired',
  /** Levou dano. */
  'damaged',
  /** Morreu. */
  'destroyed',
  /** Afastou-se do raio. */
  'out_of_range',
  /** A caixa acabou: esvaziou ou expirou. */
  'no_box',
  /** Pediu uma travessia. Sair do quadrante fecha o que estava aberto nele. */
  'crossing',
  /** A ligação caiu, ou outra tomou a cadeira. */
  'disconnected',
] as const;
export type OpenWorldLootEndReason = (typeof OPEN_WORLD_LOOT_END_REASONS)[number];

/**
 * **O conteúdo de uma caixa, dito a quem está a olhar para ela.**
 *
 * Só a quem está a saquear. Não vai na trama de estado, não vai na área de
 * interesse e não chega a quem passa ao lado: quem quiser saber o que está lá
 * dentro abre a caixa, e abrir a caixa é uma coisa que se faz parado e ao
 * alcance de quem esteja por perto.
 *
 * A **revisão** é o que torna duas atualizações concorrentes legíveis: ela sobe
 * a cada alteração, e um cliente que receba uma revisão mais baixa do que a que
 * já tem está a ver uma trama antiga que chegou fora de ordem.
 *
 * Não carrega nada sobre a conta do morto. Um `deathId` viaja porque uma
 * apresentação futura vai querer dizer de que morte a caixa veio, e ele é
 * derivado — não é um nome, não é um email e não é uma sessão.
 */
/**
 * **Uma caixa na vista, publicamente.**
 *
 * O suficiente para a desenhar e para a escolher, e nada mais: uma
 * identidade opaca, onde ela está, e quanto tempo lhe resta. **Não viaja o
 * conteúdo, não viajam os identificadores dos itens lá dentro e não viaja o
 * Ouro** — quem quiser saber o que uma caixa tem tem de a abrir, e o que ela
 * responde vai só a quem a abriu.
 *
 * Uma caixa é pública porque é um objeto do mundo: quem passa vê que alguém
 * morreu ali. O que ela guarda é outra conversa.
 */
export const openWorldBoxMarkSchema = z
  .object({
    i: openWorldBoxIdSchema,
    x: z.number().int().min(0),
    y: z.number().int().min(0),
    /** Milissegundos até expirar. Zero quando já não resta nada. */
    ttl: z.number().int().min(0),
  })
  .strict();

export type OpenWorldBoxMark = z.infer<typeof openWorldBoxMarkSchema>;

/**
 * **O conteúdo da MINHA carga**, dito só à minha conexão.
 *
 * A contagem já viajava em `cg`; o que faltava era a lista. Ela é privada por
 * inteiro: o que um piloto traz no porão não é assunto de quem voa ao lado, e
 * não há campo nenhum noutra trama onde ela pudesse aparecer.
 *
 * Só de leitura. Não há ordem para descartar, mover, equipar, consumir,
 * reorganizar ou transferir — **é a forma de essas coisas não existirem**, em
 * vez de a forma de elas serem recusadas.
 */
export const openWorldCargoContentsSchema = z
  .object({
    t: z.literal('ci'),
    i: z.array(openWorldFieldItemSchema).max(OPEN_WORLD_MAX_CARGO_SLOTS),
  })
  .strict();

export type OpenWorldCargoContents = z.infer<typeof openWorldCargoContentsSchema>;

/** Pedir a lista da própria carga. Perguntar não decide nada. */
export const openWorldCargoInspectSchema = z
  .object({
    t: z.literal('cq'),
  })
  .strict();

export type OpenWorldCargoInspect = z.infer<typeof openWorldCargoInspectSchema>;

export const openWorldBoxViewSchema = z
  .object({
    t: z.literal('lb'),
    v: z.literal(OPEN_WORLD_DEATH_BOX_VERSION),
    b: openWorldBoxIdSchema,
    /** Monotónica. Sobe a cada alteração, e nunca desce. */
    r: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    /** O ouro que lá está, em decimal canónico. Nunca um número JSON. */
    g: z.string().regex(/^(0|[1-9][0-9]*)$/),
    i: z.array(openWorldFieldItemSchema).max(OPEN_WORLD_MAX_CARGO_SLOTS),
    /** Quantos milissegundos faltam para ela desaparecer. */
    ttl: z.number().int().min(0),
  })
  .strict();

export type OpenWorldBoxView = z.infer<typeof openWorldBoxViewSchema>;

/**
 * **O que uma ordem de saque produziu.**
 *
 * Dirigida à ligação que perguntou, e carrega o id da ordem para que um cliente
 * que tenha duas em voo saiba a qual delas isto responde.
 */
export const openWorldLootResultSchema = z
  .object({
    t: z.literal('lr'),
    id: openWorldCommandIdSchema,
    /** Os ids dos itens que entraram na carga. Vazio quando nenhum entrou. */
    got: z.array(openWorldFieldItemIdSchema).max(OPEN_WORLD_MAX_CARGO_SLOTS),
    /** O ouro que entrou, em decimal canónico. `"0"` quando nenhum entrou. */
    gold: z.string().regex(/^(0|[1-9][0-9]*)$/),
    /** Quantos itens ficaram na caixa por não caberem. */
    left: z.number().int().min(0).max(OPEN_WORLD_MAX_CARGO_SLOTS),
    /** Porque não, quando não. `null` quando a ordem fez alguma coisa. */
    r: z.enum(OPEN_WORLD_LOOT_REFUSALS).nullable(),
    /**
     * Quantos espaços faltavam, quando a recusa foi `no_space`.
     *
     * Um número e não uma frase: o cliente que quiser dizer "precisas de mais
     * dois espaços" tem-no, e o que quiser dizer outra coisa também.
     */
    need: z.number().int().min(0).max(OPEN_WORLD_MAX_CARGO_SLOTS).nullable(),
  })
  .strict();

export type OpenWorldLootResult = z.infer<typeof openWorldLootResultSchema>;

/** **A interação acabou**, e porquê. Uma trama, e o socket continua vivo. */
export const openWorldLootEndedSchema = z
  .object({ t: z.literal('lx'), c: z.enum(OPEN_WORLD_LOOT_END_REASONS) })
  .strict();

/**
 * **A carga deste piloto, como ele a pode consultar.**
 *
 * Consultar é a **única** coisa que se pode fazer com a carga dentro de uma
 * expedição perigosa. Não há equipar, não há passar a um aliado, não há largar
 * no chão, não há destruir e não há trocar um item por outro para abrir espaço —
 * e nenhuma dessas coisas tem uma intenção no protocolo, o que é a forma de
 * elas não existirem em vez de a forma de serem recusadas.
 *
 * O loadout continua congelado. Um item que caia é carga, e carga não é
 * equipamento até uma extração que este gate não faz.
 */
export const openWorldCargoViewSchema = z
  .object({
    t: z.literal('cg'),
    /** Os espaços congelados. O mesmo número durante toda a expedição. */
    s: z.number().int().min(0).max(OPEN_WORLD_MAX_CARGO_SLOTS),
    /** Quantos estão ocupados. */
    u: z.number().int().min(0).max(OPEN_WORLD_MAX_CARGO_SLOTS),
    /** O ouro de campo, em decimal canónico. Nunca um número JSON. */
    g: z.string().regex(/^(0|[1-9][0-9]*)$/),
    /** A revisão da carga. Sobe a cada alteração; detecta uma trama fora de ordem. */
    r: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
  })
  .strict();

export type OpenWorldCargoView = z.infer<typeof openWorldCargoViewSchema>;
